/**
 * Keeping a terminal that stays open for days on the build that is deployed. A deploy replaces every file: a tab loaded
 * before it still runs the old code, and requests chunks (and fonts) of the old build that no longer exist.
 *
 * - A chunk that fails to load reloads the page once (`reloadForChunkError`), instead of showing the error page.
 * - `/version.json` holds the id of the build that is deployed (see webpack.config.mjs). The terminal checks it every
 *   minute and when it comes back into view (`startUpdateCheck`). A new one is offered as a reload in the top nav
 *   (`getDeployedBuildId`), and the page reloads by itself `autoReloadDelay` later, as soon as nothing blocks it
 *   (`blockAutoReload`, e.g. the terminal while an order is on screen).
 */
import {leaveBreadcrumb} from './error-reporter';

/** the query parameter a reload adds to the URL, see `reloadPage` */
const reloadParameter = 'v';

let reloading = false;

/** A reload of ours is underway: the page must not ask whether to leave it. */
export const isReloading = (): boolean => reloading;

/**
 * Reload the page. The URL gets the build id (or the time) as query parameter, so no cache hands out the index.html of
 * the old build; it is removed again as the page starts.
 */
export const reloadPage = (version: string = String(Date.now())): void => {
    const url = new URL(window.location.href);

    url.searchParams.set(reloadParameter, version);
    reloading = true;
    window.location.replace(url);
};

// not part of the route: removed before the router reads the URL (router.tsx imports this module)
const url = new URL(window.location.href);

if (url.searchParams.has(reloadParameter)) {
    url.searchParams.delete(reloadParameter);
    window.history.replaceState(window.history.state, '', url);
}

/** webpack's error for a chunk that failed to load, also mini-css-extract-plugin's for a CSS chunk */
export const isChunkLoadError = (error: unknown): boolean =>
    error instanceof Error &&
    ('ChunkLoadError' === error.name || /^Loading (css )?chunk \S+ failed/i.test(error.message));

/** the last reload for a chunk error: `{buildId, at}`, in sessionStorage so it outlives the reload */
const chunkReloadKey = 'shipping-terminal.chunk-reload';

/** a build that fails to load a chunk again this soon after reloading for one gets the error page */
const chunkReloadWindow = 60 * 1000;

/** Whether this build may reload for a chunk error, and remember it does. Without sessionStorage it never does. */
const claimChunkReload = (): boolean => {
    try {
        const last = JSON.parse(window.sessionStorage.getItem(chunkReloadKey) ?? 'null') as {
            buildId?: unknown;
            at?: unknown;
        } | null;

        if (__BUILD_ID__ === last?.buildId && Date.now() - Number(last.at) < chunkReloadWindow) {
            return false;
        }

        window.sessionStorage.setItem(chunkReloadKey, JSON.stringify({buildId: __BUILD_ID__, at: Date.now()}));

        return true;
    } catch {
        return false;
    }
};

/** the answer of `reloadForChunkError` per error: the router and the error page both ask */
const chunkErrorReloads = new WeakMap<Error, boolean>();

/**
 * A chunk failed to load, most likely because a deploy removed it: reload the page into the new build, once. Returns
 * whether it reloads; the error is then not shown nor reported, as it is expected. A build that keeps failing to load
 * its chunks, or a terminal that is offline (a reload would end on the browser's error page), gets the error page.
 */
export const reloadForChunkError = (error: unknown): boolean => {
    if (!isChunkLoadError(error)) {
        return false;
    }

    const known = chunkErrorReloads.get(error as Error);

    if (undefined !== known) {
        return known;
    }

    const reload = reloading || (navigator.onLine && claimChunkReload());

    chunkErrorReloads.set(error as Error, reload);

    if (!reload) {
        leaveBreadcrumb('No automatic reload for a chunk load error', {online: navigator.onLine});
    } else if (!reloading) {
        reloadPage();
    }

    return reload;
};

const checkInterval = 60 * 1000;

/** how long a new version is offered before the page reloads by itself (once nothing blocks it) */
const autoReloadDelay = 10 * 60 * 1000;

/** the id of the build that is deployed, when it is not this one */
let deployedBuildId: string | null = null;
/** from when the page reloads by itself, once nothing blocks it */
let autoReloadAt = Infinity;
let checking = false;

const listeners = new Set<() => void>();
const blockers = new Set<object>();

/** Reload into the deployed build, if there is a newer one. */
export const reloadToUpdate = (): void => {
    if (null !== deployedBuildId) {
        reloadPage(deployedBuildId);
    }
};

const autoReload = (): void => {
    if (Date.now() >= autoReloadAt && 0 === blockers.size && !reloading) {
        reloadToUpdate();
    }
};

const check = async (): Promise<void> => {
    if (checking) {
        return;
    }

    checking = true;

    try {
        const response = await fetch(`/version.json?t=${Date.now()}`, {cache: 'no-store'});
        const {buildId} = (await response.json()) as {buildId?: unknown};

        if (response.ok && 'string' === typeof buildId && buildId && buildId !== __BUILD_ID__) {
            if (null === deployedBuildId) {
                autoReloadAt = Date.now() + autoReloadDelay;
                // on time, so a new version that is still offered after it is held by a blocker (see the top nav)
                window.setTimeout(autoReload, autoReloadDelay);
            }

            if (buildId !== deployedBuildId) {
                deployedBuildId = buildId;
                listeners.forEach(listener => listener());
            }
        }
    } catch {
        // offline for a moment, or a deploy underway: the next check will tell
    } finally {
        checking = false;
    }

    autoReload();
};

let started = false;

/** Check for a new deploy from now on; once for the whole app (not from a component, which StrictMode mounts twice). */
export const startUpdateCheck = (): void => {
    if (started) {
        return;
    }

    started = true;
    window.setInterval(() => void check(), checkInterval);
    window.addEventListener('focus', () => void check());
    document.addEventListener('visibilitychange', () => {
        if ('visible' === document.visibilityState) {
            void check();
        }
    });
};

/** The id of the deployed build when a newer one than this is deployed, else `null`. */
export const getDeployedBuildId = (): string | null => deployedBuildId;

/** From when the page reloads by itself for the deployed build, once nothing blocks it (`Infinity` without one). */
export const getAutoReloadAt = (): number => autoReloadAt;

/** Get notified when a newer build is deployed; returns the unsubscribe function (`useSyncExternalStore`). */
export const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);

    return () => listeners.delete(listener);
};

/**
 * Keep the page from reloading by itself for a new version (a reload asked for in the top nav still does), until the
 * returned function is called. See `useBlockAutoReload`.
 */
export const blockAutoReload = (): (() => void) => {
    const blocker = {};

    blockers.add(blocker);

    return () => {
        blockers.delete(blocker);
        // not right away: the blocker may be replaced by the next one in the same commit (or StrictMode's remount)
        window.setTimeout(autoReload);
    };
};
